import { useEffect } from "react";

type Params = {
	isConflict: boolean;
	appConnected: boolean;
	isSubscriptionBroken: boolean;
	setIsConnected: (newState: boolean) => void;
	setIsSubscriptionBroken: (broken: boolean) => void;
};

/**
 * When the initial-state query reveals CONFLICT (the fridge has no active sales point), the live
 * subscription is intentionally skipped (resolveShouldSkipSubscription). But AppSync first reports
 * "no active sales point" as a FridgeSubscription transport error, so RetryLink has already flipped
 * both connection flags bad (isSubscriptionBroken true, isConnected false) by the time the
 * authoritative refetch surfaces CONFLICT. This clears the stale recovery state so the connection-error
 * modal doesn't flash and the reconnect/reload backstops don't churn behind NoActiveSalesPointModal
 * (the no-sales-point retry storm).
 *
 * Two separate concerns, because they have different safe-to-run conditions:
 *
 * 1. Assert connectivity on the CONFLICT EDGE only. A CONFLICT can only surface from a SUCCESSFUL
 *    initial-state refetch (a fridge.error payload needs a 200), which proves the network is up — so the
 *    offline flag RetryLink set from the subscription transport error is stale. Edge-triggered (deps
 *    reduce to [isConflict]) so it can NEVER force connectivity on a genuine offline that lands later
 *    while CONFLICT persists.
 *
 * 2. Clear a re-asserted broken flag WHILE conflicted AND online. The edge clear above is one-shot, but
 *    the `offline` event (ApolloClientProvider) re-asserts broken during a no-sales-point window; once
 *    DNS recovery flips connectivity back, that stale broken flag would re-arm the reconnect backoff
 *    (useReconnectAfterBrokenSubscription is gated on broken, not skip) and the reload backstop behind
 *    the modal. Clearing broken makes both bail. Gated on appConnected so it never clears broken while
 *    genuinely offline — the offline/DNS-ping lane owns that, and clearing here would strand a real
 *    outage as "connected".
 */
export const useClearRecoveryStateOnConflict = ({
	isConflict,
	appConnected,
	isSubscriptionBroken,
	setIsConnected,
	setIsSubscriptionBroken,
}: Params): void => {
	useEffect(() => {
		if (!isConflict) {
			return;
		}
		setIsSubscriptionBroken(false);
		setIsConnected(true);
	}, [isConflict, setIsConnected, setIsSubscriptionBroken]);

	useEffect(() => {
		if (isConflict && appConnected && isSubscriptionBroken) {
			setIsSubscriptionBroken(false);
		}
	}, [isConflict, appConnected, isSubscriptionBroken, setIsSubscriptionBroken]);
};
